/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { omit } from 'lodash';
import { BalanceSheetAllow, IncomeStatementAllow } from 'api';
import { SYSTEM_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import {
  BALANCE_SHEET_PROP,
  EBITDA_ALIAS,
  EXPANDED_BALANCE_SHEET_STATE,
  INCOME_STATEMENT_PROP,
  INITIAL_BALANCE_SHEET_STATE,
} from 'common/constants/financials';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { FinancialStatementPeriod } from 'common/types/financials';
import { AfterCellChanged } from 'common/types/scalarSpreadsheet/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { ICell, ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import {
  conditions as balanceSheetConditions,
  reverseParser as balanceSheetReverserParser,
} from 'pages/Financials/balance-sheet/utilities';
import {
  conditions as incomeStatementConditions,
  reverseParser as incomeStatementReverseParser,
} from 'pages/Financials/income-statement/utilities';
import afterCellChanged from 'pages/Financials/utilities/afterCellChanged';
import baseParser from 'pages/Financials/utilities/baseParser';
import trimBlankBalanceSheetColumns from 'pages/Financials/utilities/trimBlankBalanceSheetColumns';
import trimBlankIncomeStatementColumns from 'pages/Financials/utilities/trimBlankIncomeStatementColumns';
import { FinancialsReferenceGenerator } from '../../../../services/reference-generator';
import { FinancialsCellWithReferences } from '../../types/cell-with-references';
import setColumnsOnBalanceSheet from '../setColumnsOnBalanceSheet';
import setRowGroupsOnSheet from '../setRowGroupsOnSheet';
import { BalanceSheetTableData } from '../types';
import { UseIncomeStatementOrBalanceSheetDataProps } from './useIncomeStatementOrBalanceSheetData.props';


const useIncomeStatementOrBalanceSheetData = ({
  config,
  customTitlesData,
  fieldAttributes,
  isBalanceSheet = false,
  measurementDate,
  periods,
  setData,
  tableTermsData,
  currentFiscalYearEnd,
  useAdjustedEBITDA,
  isDisabled,
  isExportMode = false,
}: UseIncomeStatementOrBalanceSheetDataProps) => {
  const [cells, setCells] = useState<ICell[]>([]);
  const [columns, setColumns] = useState<FinancialStatementPeriod[]>([]);
  const [spreadsheet, setSpreadsheet] = useState<ScalarSpreadsheetConfigDataInterface | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<FinancialStatementPeriod[]>([]);
  const [tableData, setTableData] = useState<BalanceSheetTableData>();
  const isProgrammaticUpdateRef = useRef(false);

  // In export mode the balance sheet is captured with every group expanded.
  const balanceSheetInitialState = isExportMode
    ? EXPANDED_BALANCE_SHEET_STATE
    : INITIAL_BALANCE_SHEET_STATE;

  const [rowGroups, setRowGroups] = useReducer(
    rowsGroupsReducer,
    isBalanceSheet
      ? balanceSheetInitialState
      : {
        [EBITDA_ALIAS]: useAdjustedEBITDA ? USER_EXPAND : SYSTEM_COLLAPSE,
      }
  );

  const updateCells = useCallback(async newCells => {
    setCells(newCells);
  }, []);

  const customTitles = useMemo(() => {
    const newConfig = [...config];

    // Reset First Row
    newConfig[0] = {
      ...newConfig[0],
      className: customTitlesData.className,
      rowSpan: 3,
      value: customTitlesData.value,
    };

    return newConfig;
  }, [config, customTitlesData.className, customTitlesData.value]);

  const tableTerms = useMemo(
    () => ({
      columnName: tableTermsData.columnName,
      pluralColumnName: tableTermsData.pluralColumnName,
      tableName: tableTermsData.tableName,
      tableSlug: tableTermsData.tableSlug,
    }),
    [tableTermsData.columnName, tableTermsData.pluralColumnName, tableTermsData.tableName, tableTermsData.tableSlug]
  );

  // Set visible columns
  useEffect(() => {
    if (columns?.length) {
      //  NTM columns do not belong in the balance sheet
      const newVisibleColumns = columns.filter(column =>
        isBalanceSheet ? !column.is_deleted && !column.isNTM : !column.is_deleted
      );
      const getExportColumns = () =>
        isBalanceSheet
          ? trimBlankBalanceSheetColumns(newVisibleColumns)
          : trimBlankIncomeStatementColumns(newVisibleColumns, useAdjustedEBITDA);

      setVisibleColumns(isExportMode ? getExportColumns() : newVisibleColumns);
    }
  }, [columns, isBalanceSheet, isExportMode, useAdjustedEBITDA]);

  useEffect(() => {
    if (visibleColumns) {
      const tempTableData = setColumnsOnBalanceSheet({
        isBalanceSheet,
        setColumns,
        tableData: {
          columns: visibleColumns,
          measurementDate,
          currentFiscalYearEnd,
          isDisabled,
        },
      });

      setTableData(tempTableData);
    }
  }, [isBalanceSheet, measurementDate, visibleColumns, currentFiscalYearEnd, isDisabled]);

  useEffect(() => {
    if (isBalanceSheet) {
      setData({ cells, columns: visibleColumns, isDisabled });
    }
  }, [cells, isBalanceSheet, setData, visibleColumns, isDisabled]);

  useEffect(() => {
    if (periods) {
      const generatedColumns: FinancialStatementPeriod[] = [];

      periods.forEach(column => {
        const specificDataKey = isBalanceSheet ? BALANCE_SHEET_PROP : INCOME_STATEMENT_PROP;
        // include the name field in the column so saving works.
        const specificData: IncomeStatementAllow | BalanceSheetAllow =
          column[specificDataKey] ?? ({name: column.name} as IncomeStatementAllow | BalanceSheetAllow);
        const info = {
          ...column,
        };
        omit(info, [BALANCE_SHEET_PROP, INCOME_STATEMENT_PROP, 'kpi_dataset']);

        if (isBalanceSheet) {
          if (info.isLTM) {
            info.title = 'As Of';
          } else {
            const formattedDate = `${currentFiscalYearEnd?.format('MM/DD')}/${info.year || info.name}`;
            info.subtitle = formattedDate;
            if (!info.isProjectedYear) {
              info.title = formattedDate;
            }
          }
        }

        info[specificDataKey] = specificData;

        generatedColumns.push(info);
      });
      setColumns(generatedColumns);
    }
  }, [isBalanceSheet, periods, currentFiscalYearEnd]);

  const recalculateTitleCellReferences = useCallback((columnLegend: string, spreadsheets: any[]) => {
    const referenceGeneratorService = new FinancialsReferenceGenerator();

    // Find the current spreadsheet
    const spreadSheetKey = isBalanceSheet ? 'balanceSheet' : 'incomeStatement';
    const currentSpreadsheet = spreadsheets[spreadSheetKey as keyof typeof spreadsheets];
    if (!currentSpreadsheet) {
      return;
    }

    const cellsInSpreadsheet = currentSpreadsheet;

    // Collect all mapping references from cells in this column (excluding title cell at row 1)
    const columnMappingReferences: any[] = [];
    Object.keys(cellsInSpreadsheet).forEach(key => {
      if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
        const cell = cellsInSpreadsheet[key] as FinancialsCellWithReferences;
        if (cell.mappingReferences && cell.mappingReferences.length > 0) {
          columnMappingReferences.push(...cell.mappingReferences);
        }
      }
    });

    // Update the title cell with the recalculated references
    const titleCell = cellsInSpreadsheet[`${columnLegend}1`] as FinancialsCellWithReferences;
    if (titleCell) {
      if (columnMappingReferences.length === 0) {
        // No references left - clear the title cell
        titleCell.columnarReferences = [];
        titleCell.columnarReferencesMetadata = [];
        titleCell.valueViewer = null;
        titleCell.dispatchEvent();
      } else {
        // Update with new references
        const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
        titleCell.columnarReferences = docRefsData.documentIds;
        titleCell.columnarReferencesMetadata = docRefsData.metadata as any;
        titleCell.dispatchEvent();
      }
    }
  }, [isBalanceSheet]);

  const handleCellChanged = useCallback<AfterCellChanged>((
    initialChanges,
    cells,
    rowConfig,
    tableData,
    spreadsheets
  ) => {

    // Skip clearing logic if this is a programmatic update from pushing mappings
    if (!isProgrammaticUpdateRef.current) {
      initialChanges.forEach(change => {
        const newValue = change.value;
        const isCellReferencedCell = (change.cell as FinancialsCellWithReferences).mappingReferences?.length > 0;
        if (isCellReferencedCell) {
          const shouldRemoveMappingRefs = +newValue !== +change.cell.value && +change.cell.value !== 0;
          if (shouldRemoveMappingRefs) {
            // Clear the cell's mapping references (only on manual edits)
            (change.cell as FinancialsCellWithReferences).mappingReferences = [];
            (change.cell as FinancialsCellWithReferences).columnarReferences = [];
            (change.cell as FinancialsCellWithReferences).columnarReferencesMetadata = [];
            change.cell.className = '';
            change.cell.valueViewer = null;
            change.cell.dispatchEvent();

            // Recalculate the title cell's references for this column
            const { columnLegend } = (change.cell as any);
            if (columnLegend) {
              recalculateTitleCellReferences(columnLegend, spreadsheets);
            }
          }
        }
      });
    }

    return afterCellChanged(
      initialChanges as any,
      cells as any,
      rowConfig as any
    );
  }, [recalculateTitleCellReferences]);

  useEffect(() => {
    if (visibleColumns && tableData) {
      setSpreadsheet(
        // any is used here as spreadsheet config is not strongly typed
        new SpreadsheetConfig({
          afterCellChanged: handleCellChanged,
          allowConfirmAndDeleteColumn: !isDisabled,
          columns: visibleColumns,
          conditions: isBalanceSheet ? balanceSheetConditions : incomeStatementConditions,
          customTitles,
          fieldAttributes: isBalanceSheet ? fieldAttributes.balanceSheetAttrs : fieldAttributes.incomeStatementAttrs,
          hasColTitle: false,
          name: isBalanceSheet ? 'balanceSheet' : 'incomeStatement',
          parser: baseParser,
          reverseParser: isBalanceSheet ? balanceSheetReverserParser : incomeStatementReverseParser,
          rowConfig: config,
          setColumns,
          setDataSource: isBalanceSheet ? undefined : setData,
          showPreviousColsDivider: true,
          showToolbar: true,
          showTotalColumn: false,
          tableData,
          tableTerms,
          allowCopyColumn: !isDisabled,
          allowDeleteColumn: !isDisabled,
        })
      );
    }
  }, [
    config,
    customTitles,
    fieldAttributes.balanceSheetAttrs,
    fieldAttributes.incomeStatementAttrs,
    isBalanceSheet,
    setData,
    tableData,
    tableTerms,
    visibleColumns,
    isDisabled,
    handleCellChanged,
  ]);

  const setProgrammaticUpdate = useCallback((isProgrammatic: boolean) => {
    isProgrammaticUpdateRef.current = isProgrammatic;
  }, []);

  const incomeStatementOrBalanceSheetData = {
    cells,
    config,
    customTitles,
    setColumns,
    spreadsheet,
    tableTerms,
    updateCells,
    visibleColumns,
    setProgrammaticUpdate,
  };

  return setRowGroupsOnSheet({
    incomeStatementOrBalanceSheetData,
    rowGroups,
    setRowGroups,
  });
};

export default useIncomeStatementOrBalanceSheetData;
